import { newFileContainerId } from './FileContainerId';
import type {
  FileContainerId,
  FileContainerJSON,
  GroupId,
  ModelId,
  UserId,
  ViewModelAssetId,
  ViewModelId,
} from 'balus-common';
import { RTDBPath } from 'balus-common';
import { Size } from '@view-model/models/common/basic';
import { Rect } from '@view-model/models/common/basic/Rect';
import type { Point } from '@view-model/models/common/basic';
import { ObjectRepository } from '@framework/repository';
import type { ICommand } from '@model-framework/command';
import { CreateCommand } from '@model-framework/command';
import { Timestamp } from '@framework/Timestamp';
import { ViewModelAssetURL } from '@user/pages/urls';

// 要素の複製では実体もコピーするため、複製先で発行した assetId と複製した人に付け替える。
// 別ビューへの移動では同じ実体を指したまま作成者も保つので渡さない。
export type FileContainerAssetCopy = {
  assetIdMap: Record<ViewModelAssetId, ViewModelAssetId>;
  createdUserId: UserId;
};

export class FileContainer {
  constructor(
    public readonly id: FileContainerId,
    public readonly assetId: ViewModelAssetId,
    public readonly filename: string,
    public readonly fileSize: number,
    public readonly contentType: string,
    public readonly size: Size,
    public readonly createdUserId: UserId,
    public readonly createdAt: Timestamp
  ) {}

  dump(): FileContainerJSON {
    const { id, assetId, filename, fileSize, contentType, size, createdUserId, createdAt } = this;
    return {
      id,
      assetId,
      filename,
      fileSize,
      contentType,
      size: size.dump(),
      createdUserId,
      createdAt: createdAt.toUnixTimestamp(),
    };
  }

  static load(dump: FileContainerJSON): FileContainer {
    const { id, assetId, filename, fileSize, contentType, size, createdUserId, createdAt } = dump;
    return new FileContainer(
      id,
      assetId,
      filename,
      fileSize,
      contentType,
      Size.load(size),
      createdUserId,
      Timestamp.fromUnixTimestamp(createdAt)
    );
  }

  static buildNew(attrs: {
    assetId: ViewModelAssetId;
    filename: string;
    fileSize: number;
    contentType: string;
    size: Size;
    createdUserId: UserId;
  }): FileContainer {
    const { assetId, filename, fileSize, contentType, size, createdUserId } = attrs;
    return new FileContainer(
      newFileContainerId(),
      assetId,
      filename,
      fileSize,
      contentType,
      size,
      createdUserId,
      Timestamp.now()
    );
  }

  isEqual(other: FileContainer): boolean {
    return (
      this.id === other.id &&
      this.assetId === other.assetId &&
      this.filename === other.filename &&
      this.fileSize === other.fileSize &&
      this.contentType === other.contentType &&
      this.size.isEqual(other.size) &&
      this.createdUserId === other.createdUserId &&
      this.createdAt.toUnixTimestamp() === other.createdAt.toUnixTimestamp()
    );
  }

  cloneNew(newId: FileContainerId, createdAt: Timestamp, assetCopy?: FileContainerAssetCopy): FileContainer {
    const { filename, fileSize, contentType, size } = this;
    const assetId = assetCopy ? assetCopy.assetIdMap[this.assetId] : this.assetId;
    const createdUserId = assetCopy ? assetCopy.createdUserId : this.createdUserId;
    return new FileContainer(newId, assetId, filename, fileSize, contentType, size, createdUserId, createdAt);
  }

  isImage(): boolean {
    return this.contentType.startsWith('image/');
  }

  getRect(position: Point): Rect {
    return new Rect(position, this.size);
  }

  assetUrl(groupId: GroupId, viewModelId: ViewModelId, baseUrl: string): string {
    return new ViewModelAssetURL(groupId, viewModelId, this.assetId).url(baseUrl);
  }

  downloadUrl(groupId: GroupId, viewModelId: ViewModelId, baseUrl: string): string {
    const url = new URL(this.assetUrl(groupId, viewModelId, baseUrl));
    url.searchParams.set('download', '1');
    return url.toString();
  }

  buildCreateCommand(viewModelId: ViewModelId, modelId: ModelId): ICommand {
    const repository = new ObjectRepository(
      FileContainer,
      RTDBPath.FileContainer.containerPath(viewModelId, modelId, this.id)
    );
    return new CreateCommand(this, repository);
  }
}
